import { Skeleton } from "@mui/material";
import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import {
  Box,
  ContentRepeater,
  FlexColBox,
  Icon,
  OverlayScrollbarWrapper,
} from "../../../common/components";
import { useSelector } from "../../../common/hooks/useSelector";
import DesignPill from "../../../components/StudioToolbar/components/DesignPill";
import { STUDIO_TOOLBAR_HEIGHT } from "../../../components/StudioToolbar/StudioToolbar";
import { useStudio } from "../../../context/Studio/useStudio";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { ROUTES } from "../../../constant/common.constant";
import { useTeam } from "../../../context/Team";
import { sortDesigns } from "../../../utils/SortingUtils";
import { useProject } from "../../../context/Project/useProject";

const styles = createStyles({
  container: {
    p: { xs: spacing.mediumMobile, lg: spacing.mediumDesktop },
    background: { xs: "white", lg: palette.grey.lightBackground },
    borderRight: { xs: "none", lg: `1px solid ${palette.grey.lightGrey}` },
    width: "fit-content",
    minWidth: { xs: "100%", lg: "77px" },
    gap: "12px",
    minHeight: {
      xs: "auto",
      lg: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
    },
    flexDirection: { xs: "row", lg: "column" },
  },
});

interface Props {
  onAddButtonClick?: () => void;
}

const ProjectDesignSelector = (props: Props) => {
  const { onAddButtonClick } = props;
  const {
    projectId,
    designId: selectedDesignId,
    isLoading,
    designState: { images },
    editorConfig,
  } = useStudio();
  const { teamId } = useTeam();
  const { canAddDesign } = useSelector((state) => state.userInfo);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { project, updateProjectDesignImages } = useProject();

  const shownDesigns = useMemo(() => {
    if (!projectId || editorConfig?.toolbar?.hideDesignSelector) {
      return [];
    }

    return project?.designs.sort(sortDesigns);
  }, [projectId, project, editorConfig]);

  useEffect(() => {
    if (images && images.length > 0) {
      updateProjectDesignImages(selectedDesignId, images);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  if (isLoading) {
    return (
      <Box
        sx={{
          maxHeight: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
        }}
      >
        <FlexColBox sx={styles.container}>
          <ContentRepeater repeatCount={10}>
            <Skeleton
              variant="rounded"
              width={52}
              height={46}
              sx={{ borderRadius: "8px" }}
            />
          </ContentRepeater>
        </FlexColBox>
      </Box>
    );
  }

  return (
    <OverlayScrollbarWrapper>
      <Box
        sx={{
          maxHeight: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
        }}
      >
        <FlexColBox sx={styles.container}>
          {canAddDesign && onAddButtonClick && (
            <DesignPill
              onClick={onAddButtonClick}
              data-test="projectDesignSelector.addDesignButton"
            >
              <Icon
                icon="add"
                style={{ color: palette.grey.primary }}
                size={24}
              />
            </DesignPill>
          )}
          {shownDesigns?.map((d) =>
            //If the design is in error, then we don't allow to use it
            d.errorKey ? (
              <DesignPill
                key={d.id}
                designImages={d.images}
                designErrorKey={d.errorKey}
                tooltip={t("design.design_with_error")}
              />
            ) : (
              <DesignPill
                active={selectedDesignId === d.id}
                key={d.id}
                designImages={d.images}
                designErrorKey={d.errorKey}
                tooltip={d.name}
                onClick={() =>
                  navigate(
                    generatePath(ROUTES.STUDIO, {
                      designId: d.id,
                      projectId,
                      teamId,
                    })
                  )
                }
              />
            )
          )}
        </FlexColBox>
      </Box>
    </OverlayScrollbarWrapper>
  );
};

export default memo(ProjectDesignSelector);
